import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Database, Globe, FileText, MapPin, Building2, DollarSign, Users, Briefcase, Award, RefreshCw } from 'lucide-react'

interface LeadStats {
  total_leads: number
  total_countries: number
  with_description_en: number
  with_description_tr: number
  with_coordinates: number
  with_website: number
  with_email: number
  with_phone: number
  with_employees: number
  with_revenue: number
  with_services: number
  with_certificates: number
  with_image: number
  by_type: { type: string; count: number }[]
  by_status: { status: string; count: number }[]
  by_country: { country: string; count: number }[]
  by_employee_range: { range: string; count: number }[]
  by_revenue_range: { range: string; count: number }[]
}

async function fetchLeadStats(): Promise<LeadStats> {
  // Get all leads with necessary fields for counting
  const { data: allLeads, count: total_leads } = await supabase
    .from('leads')
    .select('country, type, status, employee_range, revenue_range, description_en, description_tr, latitude, longitude, website, email, phone, employees, revenue, services, certificates, image_url', { count: 'exact' })

  const leads = allLeads || []

  // Calculate field counts
  const with_description_en = leads.filter(l => l.description_en && l.description_en.trim() !== '').length
  const with_description_tr = leads.filter(l => l.description_tr && l.description_tr.trim() !== '').length
  const with_coordinates = leads.filter(l => l.latitude != null && l.longitude != null).length
  const with_website = leads.filter(l => l.website && l.website.trim() !== '').length
  const with_email = leads.filter(l => l.email && l.email.trim() !== '').length
  const with_phone = leads.filter(l => l.phone && l.phone.trim() !== '').length
  const with_employees = leads.filter(l => l.employees != null).length
  const with_revenue = leads.filter(l => l.revenue != null).length
  const with_services = leads.filter(l => l.services && l.services.length > 0).length
  const with_certificates = leads.filter(l => l.certificates && l.certificates.length > 0).length
  const with_image = leads.filter(l => l.image_url && l.image_url.trim() !== '').length

  // Get unique countries
  const uniqueCountries = new Set(leads.map(l => l.country).filter(Boolean)).size

  // Group by type
  const typeCount: Record<string, number> = {}
  leads.forEach(l => {
    const key = l.type || 'unknown'
    typeCount[key] = (typeCount[key] || 0) + 1
  })
  const byType = Object.entries(typeCount)
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count)

  // Group by status
  const statusCount: Record<string, number> = {}
  leads.forEach(l => {
    const key = l.status || 'unknown'
    statusCount[key] = (statusCount[key] || 0) + 1
  })
  const byStatus = Object.entries(statusCount)
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => b.count - a.count)

  // Group by country (top 20)
  const countryCount: Record<string, number> = {}
  leads.forEach(l => {
    if (l.country) {
      countryCount[l.country] = (countryCount[l.country] || 0) + 1
    }
  })
  const byCountry = Object.entries(countryCount)
    .map(([country, count]) => ({ country, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20)

  // Group by employee range
  const empRangeCount: Record<string, number> = {}
  leads.forEach(l => {
    const key = l.employee_range || 'Unknown'
    empRangeCount[key] = (empRangeCount[key] || 0) + 1
  })
  const byEmployeeRange = Object.entries(empRangeCount)
    .map(([range, count]) => ({ range, count }))
    .sort((a, b) => b.count - a.count)

  // Group by revenue range
  const revRangeCount: Record<string, number> = {}
  leads.forEach(l => {
    const key = l.revenue_range || 'Unknown'
    revRangeCount[key] = (revRangeCount[key] || 0) + 1
  })
  const byRevenueRange = Object.entries(revRangeCount)
    .map(([range, count]) => ({ range, count }))
    .sort((a, b) => b.count - a.count)

  return {
    total_leads: total_leads || 0,
    total_countries: uniqueCountries,
    with_description_en,
    with_description_tr,
    with_coordinates,
    with_website,
    with_email,
    with_phone,
    with_employees,
    with_revenue,
    with_services,
    with_certificates,
    with_image,
    by_type: byType,
    by_status: byStatus,
    by_country: byCountry,
    by_employee_range: byEmployeeRange,
    by_revenue_range: byRevenueRange,
  }
}

function StatCard({ icon: Icon, label, value, total, color = 'primary' }: {
  icon: React.ElementType
  label: string
  value: number
  total: number
  color?: 'primary' | 'green' | 'blue' | 'orange' | 'purple'
}) {
  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0'
  const colorClasses = {
    primary: 'bg-primary-100 text-primary-600',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    orange: 'bg-orange-100 text-orange-600',
    purple: 'bg-purple-100 text-purple-600',
  }

  return (
    <div className="bg-white rounded-lg border border-secondary-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-secondary-500 truncate">{label}</p>
          <p className="text-xl font-semibold text-secondary-900">
            {value.toLocaleString()}
            <span className="text-sm font-normal text-secondary-400 ml-1">/ {total.toLocaleString()}</span>
          </p>
        </div>
        <div className="text-right">
          <span className={`text-sm font-medium ${Number(percentage) > 50 ? 'text-green-600' : 'text-orange-600'}`}>
            {percentage}%
          </span>
        </div>
      </div>
    </div>
  )
}

function DistributionTable({ title, data, icon: Icon }: {
  title: string
  data: { [key: string]: string | number }[]
  icon: React.ElementType
}) {
  const total = data.reduce((sum, item) => sum + (Object.values(item)[1] as number), 0)

  return (
    <div className="bg-white rounded-lg border border-secondary-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-secondary-200 flex items-center gap-2">
        <Icon className="w-5 h-5 text-secondary-500" />
        <h3 className="font-medium text-secondary-900">{title}</h3>
      </div>
      <div className="max-h-[300px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary-50 sticky top-0">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-secondary-600">Value</th>
              <th className="text-right px-4 py-2 font-medium text-secondary-600">Count</th>
              <th className="text-right px-4 py-2 font-medium text-secondary-600">%</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {data.map((item, index) => {
              const keys = Object.keys(item)
              const label = item[keys[0]] as string
              const count = item[keys[1]] as number
              const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0'
              return (
                <tr key={index} className="hover:bg-secondary-50">
                  <td className="px-4 py-2 text-secondary-900">{label || '-'}</td>
                  <td className="px-4 py-2 text-right text-secondary-600">{count.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right text-secondary-500">{pct}%</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function LeadStatisticsPage() {
  const { data: stats, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['lead-statistics'],
    queryFn: fetchLeadStats,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="text-center text-secondary-500 py-12">
        Failed to load statistics
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold text-secondary-900">Lead Statistics</h1>
          <span className="text-secondary-400 hidden sm:inline">|</span>
          <p className="text-secondary-500 text-sm hidden sm:block">Database overview</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-primary-600 text-white rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Database className="w-8 h-8 opacity-80" />
            <div>
              <p className="text-primary-100 text-sm">Total Leads</p>
              <p className="text-3xl font-bold">{stats.total_leads.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-blue-600 text-white rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Globe className="w-8 h-8 opacity-80" />
            <div>
              <p className="text-blue-100 text-sm">Countries</p>
              <p className="text-3xl font-bold">{stats.total_countries}</p>
            </div>
          </div>
        </div>
        <div className="bg-green-600 text-white rounded-lg p-4">
          <div className="flex items-center gap-3">
            <MapPin className="w-8 h-8 opacity-80" />
            <div>
              <p className="text-green-100 text-sm">With Coordinates</p>
              <p className="text-3xl font-bold">{stats.with_coordinates.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-purple-600 text-white rounded-lg p-4">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 opacity-80" />
            <div>
              <p className="text-purple-100 text-sm">With Description</p>
              <p className="text-3xl font-bold">{stats.with_description_en.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Field Coverage */}
      <div>
        <h2 className="text-lg font-semibold text-secondary-900 mb-4">Data Coverage</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard icon={FileText} label="English Description" value={stats.with_description_en} total={stats.total_leads} color="purple" />
          <StatCard icon={FileText} label="Turkish Description" value={stats.with_description_tr} total={stats.total_leads} color="purple" />
          <StatCard icon={MapPin} label="Coordinates (Lat/Lng)" value={stats.with_coordinates} total={stats.total_leads} color="green" />
          <StatCard icon={Globe} label="Website" value={stats.with_website} total={stats.total_leads} color="blue" />
          <StatCard icon={Building2} label="Email" value={stats.with_email} total={stats.total_leads} color="blue" />
          <StatCard icon={Building2} label="Phone" value={stats.with_phone} total={stats.total_leads} color="blue" />
          <StatCard icon={Users} label="Employee Count" value={stats.with_employees} total={stats.total_leads} color="orange" />
          <StatCard icon={DollarSign} label="Revenue" value={stats.with_revenue} total={stats.total_leads} color="orange" />
          <StatCard icon={Briefcase} label="Services" value={stats.with_services} total={stats.total_leads} color="primary" />
          <StatCard icon={Award} label="Certificates" value={stats.with_certificates} total={stats.total_leads} color="primary" />
          <StatCard icon={FileText} label="Image" value={stats.with_image} total={stats.total_leads} color="green" />
        </div>
      </div>

      {/* Distribution Tables */}
      <div>
        <h2 className="text-lg font-semibold text-secondary-900 mb-4">Data Distribution</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <DistributionTable title="By Type" data={stats.by_type} icon={Briefcase} />
          <DistributionTable title="By Status" data={stats.by_status} icon={Database} />
          <DistributionTable title="Top 20 Countries" data={stats.by_country} icon={Globe} />
          <DistributionTable title="By Employee Range" data={stats.by_employee_range} icon={Users} />
          <DistributionTable title="By Revenue Range" data={stats.by_revenue_range} icon={DollarSign} />
        </div>
      </div>
    </div>
  )
}
